import * as React from 'react';
import { createPortal } from 'react-dom';
import type { PlaybookArchiveRow } from '../../utils/playbookRecordIndex';
import { detectTextDirection } from '../../utils/textUtils';
import {
  PlaybookRecordClausePreview,
  type PlaybookRecordPreviewData,
} from './PlaybookRecordClausePreview';

const POPOVER_WIDTH = 300;
const POPOVER_MAX_HEIGHT = 220;
const VIEWPORT_PAD = 12;
const ANCHOR_GAP = 8;

export type RecordCitationLinkProps = {
  recordNum: number;
  label: React.ReactNode;
  row: PlaybookArchiveRow;
  loadPreview: (row: PlaybookArchiveRow) => Promise<PlaybookRecordPreviewData>;
  onOpenSource: (recordNum: number) => void;
};

type PopoverPlacement = {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
  placeAbove: boolean;
};

function computePopoverPlacement(
  anchorRect: DOMRect,
  viewportWidth: number,
  viewportHeight: number,
): PopoverPlacement {
  const width = Math.min(POPOVER_WIDTH, viewportWidth - VIEWPORT_PAD * 2);
  const spaceAbove = anchorRect.top - VIEWPORT_PAD;
  const spaceBelow = viewportHeight - anchorRect.bottom - VIEWPORT_PAD;
  const placeAbove = spaceAbove >= spaceBelow && spaceAbove >= 72;

  const maxHeight = Math.min(
    POPOVER_MAX_HEIGHT,
    (placeAbove ? spaceAbove : spaceBelow) - ANCHOR_GAP,
  );

  const top = placeAbove
    ? anchorRect.top - ANCHOR_GAP
    : anchorRect.bottom + ANCHOR_GAP;

  let left = anchorRect.left + anchorRect.width / 2 - width / 2;
  if (left < VIEWPORT_PAD) left = VIEWPORT_PAD;
  if (left + width > viewportWidth - VIEWPORT_PAD) {
    left = viewportWidth - VIEWPORT_PAD - width;
  }

  return { top, left, width, maxHeight: Math.max(80, maxHeight), placeAbove };
}

export const RecordCitationLink: React.FC<RecordCitationLinkProps> = ({
  recordNum,
  label,
  row,
  loadPreview,
  onOpenSource,
}) => {
  const anchorRef = React.useRef<HTMLSpanElement>(null);
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const placementRef = React.useRef<PopoverPlacement | null>(null);
  const [open, setOpen] = React.useState(false);
  const [preview, setPreview] = React.useState<PlaybookRecordPreviewData | null>(null);
  const [placement, setPlacement] = React.useState<PopoverPlacement | null>(null);
  const hoverTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const leaveTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const previewDir = React.useMemo(
    () => detectTextDirection(row.paragraph_text || row.paragraph_preview || ''),
    [row.paragraph_text, row.paragraph_preview],
  );

  const syncPlacementFromAnchor = React.useCallback((lockSide: boolean) => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const next = computePopoverPlacement(
      anchor.getBoundingClientRect(),
      window.innerWidth,
      window.innerHeight,
    );
    if (lockSide && placementRef.current) {
      next.placeAbove = placementRef.current.placeAbove;
      next.top = placementRef.current.placeAbove
        ? anchor.getBoundingClientRect().top - ANCHOR_GAP
        : anchor.getBoundingClientRect().bottom + ANCHOR_GAP;
    }
    placementRef.current = next;
    setPlacement(next);
  }, []);

  const cancelHide = React.useCallback(() => {
    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
  }, []);

  const openPreview = React.useCallback(() => {
    cancelHide();
    syncPlacementFromAnchor(false);
    setOpen(true);
    setPreview((prev) =>
      prev ?? {
        plainText: row.paragraph_text?.trim() || row.paragraph_preview || null,
        side: row.side === 'our_side' || row.side === 'their_side' ? row.side : null,
        loading: true,
      },
    );
    void loadPreview(row).then((data) => {
      setPreview(data);
    });
  }, [cancelHide, loadPreview, row, syncPlacementFromAnchor]);

  const scheduleShow = React.useCallback(() => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(openPreview, 280);
  }, [openPreview]);

  const scheduleHide = React.useCallback(() => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    leaveTimer.current = setTimeout(() => {
      setOpen(false);
      setPlacement(null);
      placementRef.current = null;
    }, 150);
  }, []);

  React.useLayoutEffect(() => {
    if (!open) return;
    const onReflow = () => syncPlacementFromAnchor(true);
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [open, syncPlacementFromAnchor]);

  React.useEffect(
    () => () => {
      if (hoverTimer.current) clearTimeout(hoverTimer.current);
      if (leaveTimer.current) clearTimeout(leaveTimer.current);
    },
    [],
  );

  const popover =
    open && preview && placement ? (
      <div
        ref={popoverRef}
        id={`record-preview-${recordNum}`}
        className={`ask-md-record-preview-popover ask-md-record-preview-popover--fixed${
          placement.placeAbove ? ' ask-md-record-preview-popover--above' : ''
        }`}
        role="tooltip"
        dir={previewDir}
        style={{
          position: 'fixed',
          top: placement.top,
          left: placement.left,
          width: placement.width,
          maxHeight: placement.maxHeight,
          zIndex: 10050,
          transform: placement.placeAbove ? 'translateY(-100%)' : undefined,
        }}
        onMouseEnter={cancelHide}
        onMouseLeave={scheduleHide}
      >
        <PlaybookRecordClausePreview data={preview} />
      </div>
    ) : null;

  return (
    <span
      ref={anchorRef}
      className="ask-md-record-link-wrap"
      onMouseEnter={scheduleShow}
      onMouseLeave={scheduleHide}
      onFocus={scheduleShow}
      onBlur={scheduleHide}
    >
      <button
        type="button"
        className="ask-md-record-link"
        onClick={() => onOpenSource(recordNum)}
        aria-describedby={open ? `record-preview-${recordNum}` : undefined}
      >
        {label}
      </button>
      {typeof document !== 'undefined' && popover
        ? createPortal(popover, document.body)
        : popover}
    </span>
  );
};
